import React, { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";

const steps = [
  {
    id: "01",
    title: "Submit Your Inquiry",
    desc: "Share your research requirements through our RFQ form or speak directly with our scientific team.",
  },
  {
    id: "02",
    title: "Feasibility Assessment",
    desc: "We review availability, timeline, and procurement strategy for your specific specimen needs.",
  },
  {
    id: "03",
    title: "Receive Detailed Quote",
    desc: "Get transparent pricing with specimen details, data annotations, and delivery timeline.",
  },
  {
    id: "04",
    title: "Collection & Quality Check",
    desc: "Specimens collected per protocol, processed following SOPs, quality validated before release.",
  },
  {
    id: "05",
    title: "Secure Shipment",
    desc: "Temperature-controlled shipping with tracking, documentation, and import/export support if needed.",
  },
];

const StepDot = ({ index, scrollYProgress, id }) => {
  // Har step ka active point calculation
  const stepSection = 1 / steps.length;
  const start = index * stepSection;
  const end = (index + 0.5) * stepSection;

  const color = useTransform(
    scrollYProgress,
    [start, end],
    ["#00000033", "#22d3ee"],
  );

  const scale = useTransform(
    scrollYProgress,
    [start, end, end + stepSection],
    [1, 1.2, 1],
  );

  return (
    <div className="relative flex flex-col items-center">
      <div className="w-6 h-6 md:w-[30px] md:h-[30px] 2xl:w-[60px] 2xl:h-[60px] rounded-full bg-white border-4 border-[#102463] z-20 flex items-center justify-center shadow-lg">
        <motion.div
          style={{ backgroundColor: color, scale }}
          className="w-2 h-2 md:w-[10px] md:h-[10px] 2xl:w-[27px] 2xl:h-[27px] rounded-full #00000033"
        />
      </div>
      <span className="absolute -bottom-8 text-white font-bold text-[10px] md:text-[16px] 2xl:text-[24px] tracking-widest whitespace-nowrap">
        Step {id}
      </span>
    </div>
  );
};

const ContentCard = ({ index, scrollYProgress, title, desc }) => {
  const stepSection = 1 / steps.length;
  const start = index * stepSection;
  const middle = index * stepSection + stepSection / 2;
  const end = (index + 1) * stepSection;

  // Fade in and Fade out logic
  const opacity = useTransform(
    scrollYProgress,
    [start, middle, end],
    [0, 1, 0],
  );

  const y = useTransform(scrollYProgress, [start, middle, end], [0, 0, 0]);

  return (
    <motion.div
      style={{ opacity, y, pointerEvents: "none" }}
      className="absolute inset-0 bg-[#FFFFFF33] rounded-t-[30px] md:rounded-t-[40px] p-6 md:p-8 text-center flex flex-col justify-center border-t-2 border-cyan-400/50 shadow-2xl "
    >
      <h4 className="text-white text-lg md:text-[24px] 2xl:text-[30px] font-bold mb-2 uppercase tracking-wide">
        {title}
      </h4>
      <p className="text-[#FFFFFF] text-xs md:text-[20px] 2xl:text-[24px] leading-relaxed ">
        {desc}
      </p>
    </motion.div>
  );
};

const ProcurementJourney = () => {
  const targetRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: targetRef,
    // "start end" -> Animation tab shuru hogi jab target ka 'start' screen ke 'end' (bottom) par aayega
    // "end start" -> Animation tab khatam hogi jab target ka 'end' screen ke 'start' (top) par aayega
    offset: ["start center", "end center"],
  });

  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

  return (
    /* h-[300vh] controls how long the scroll lasts. Adjust as needed */
    <section ref={targetRef} className="relative h-[300vh] ">
      <div className="sticky top-[15%] md:top-[18%]  w-full flex flex-col items-center justify-center bg-[#102463] overflow-hidden max-w-[1800px] mx-auto rounded-xl  px-4 md:px-6">
        {/* Header Section */}
        <div className="text-center mb-16 md:mb-24  pt-10">
          <h2 className="text-white text-[24px] md:text-[30px] lg:text-[40px] 2xl:text-[60px] font-semibold leading-normal ">
            For Researchers:
          </h2>
          <h3 className="text-white text-[24px] md:text-[30px] lg:text-[40px] 2xl:text-[60px] font-semibold  leading-normal">
            Biospecimen Procurement Journey
          </h3>
        </div>

        {/* Timeline Area */}
        <div className="relative w-full max-w-[1444px]  mb-20">
          {/* Background Static Line */}
          <div className="absolute top-1/2 left-0 w-full h-1 bg-white/10 -translate-y-1/2" />

          {/* Animated Glow Line */}
          <motion.div
            style={{ scaleX }}
            className="absolute top-1/2 left-0 w-full h-1 bg-cyan-400 origin-left -translate-y-1/2 shadow-[0_0_20px_#22d3ee]"
          />

          <div className="relative flex justify-between items-center w-full">
            {steps.map((step, index) => (
              <StepDot
                key={step.id}
                index={index}
                id={step.id}
                scrollYProgress={scrollYProgress}
              />
            ))}
          </div>
        </div>

        {/* Content Box Area */}
        <div className="w-full max-w-sm md:max-w-[892px] h-40 md:h-48 relative mt-[40px]">
          {steps.map((step, index) => (
            <ContentCard
              key={step.id}
              index={index}
              title={step.title}
              desc={step.desc}
              scrollYProgress={scrollYProgress}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProcurementJourney;
